<template>
  <div class="action-bar" :class="{ 'overflow-hidden': disableOverflow }">
    <transition
      :name="getTransitionName"
      @enter="handleDisableOverflow"
      @after-enter="handleEnableOverflow"
      @leave="handleDisableOverflow"
      @after-leave="handleEnableOverflow"
    >
      <!--
        Each state is wrapped in a bare container that owns the enter/leave
        animation, so the inner content (and its padding) is never animated.
      -->
      <div v-if="!hasChanges" key="action-bar" class="bar-state">
        <div class="bar-wrapper">
          <slot name="action-bar" />
        </div>
      </div>

      <div v-else key="save-bar" class="bar-state">
        <v-row no-gutters justify="end" align="center" class="bar-wrapper">
          <v-col v-if="$listeners['delete']">
            <v-tooltip top>
              <template #activator="{ on }">
                <vs-button
                  icon="delete"
                  type="tertiary"
                  destructive
                  data-test="delete-action"
                  v-on="on"
                  @click="$emit('delete')"
                />
              </template>
              Delete
            </v-tooltip>
          </v-col>

          <v-col v-if="hasChanges && $listeners['save']" class="mr-4">
            <vs-text type="subdued" data-test="unsaved-message">
              {{ changesLabel }}
            </vs-text>
          </v-col>

          <v-col v-if="hasChanges && $listeners['discard']" class="shrink mr-2">
            <vs-button
              type="secondary"
              :label="discardLabel"
              data-test="discard-action"
              @click="$emit('discard')"
            />
          </v-col>

          <v-col v-if="hasChanges && $listeners['save'] && !hideSaveButton" class="shrink">
            <vs-button
              data-test="save-action"
              :loading="isSaving"
              :label="saveLabel"
              @click="$emit('save')"
            />
          </v-col>
        </v-row>
      </div>
    </transition>
  </div>
</template>

<script>
import VsText from '@/components/vision/VsText.vue'
import VsButton from '@/components/vision/VsButton.vue'

export default {
  components: { VsText, VsButton },
  props: {
    saveLabel: {
      type: String,
      default: 'Save',
    },
    discardLabel: {
      type: String,
      default: 'Discard',
    },
    changesLabel: {
      type: String,
      default: 'You have unsaved changes',
    },
    isSaving: {
      type: Boolean,
      default: false,
    },
    hasChanges: {
      type: Boolean,
      default: false,
    },
    hideSaveButton: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      disableOverflow: false,
    }
  },
  computed: {
    getTransitionName() {
      return this.$slots['action-bar'] && !this.hasChanges ? 'slide-fade-down' : 'slide-fade-up'
    },
  },
  methods: {
    handleEnableOverflow() {
      this.disableOverflow = false
    },
    handleDisableOverflow() {
      this.disableOverflow = true
    },
  },
}
</script>

<style scoped>
/*
 * The leaving and entering states animate at the same time: one collapses to
 * height 0 while the other grows from 0. With matching duration and easing,
 * their heights sum to a smooth blend of the old and new bar height.
 *
 * Animating to/from `height: auto` needs `interpolate-size`. Browsers without
 * it snap the height instead, so the leaving state disappears immediately
 * while the entering one still fades and slides in. With reduced motion there
 * are no transitions at all, so the states swap instantly.
 */
@media (prefers-reduced-motion: no-preference) {
  .action-bar {
    interpolate-size: allow-keywords;
  }

  .slide-fade-up-enter-active,
  .slide-fade-up-leave-active,
  .slide-fade-down-enter-active,
  .slide-fade-down-leave-active {
    overflow: clip;
    transition-property: opacity, transform, height;
    transition-duration: 200ms;
    transition-timing-function: ease-out;
    will-change: transform;
  }

  .slide-fade-up-enter,
  .slide-fade-up-leave-to,
  .slide-fade-down-enter,
  .slide-fade-down-leave-to {
    height: 0;
    opacity: 0;
  }

  .slide-fade-up-enter {
    transform: translateY(var(--space-small));
  }

  .slide-fade-up-leave-to {
    transform: translateY(calc(var(--space-small) * -1));
  }

  .slide-fade-down-enter {
    transform: translateY(calc(var(--space-small) * -1));
  }

  .slide-fade-down-leave-to {
    transform: translateY(var(--space-small));
  }
}
</style>
